import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, Radio, Clock, Coins, Mic, Video } from 'lucide-react';
import { mediaUrl } from '../api/posts';
import CallCreatorSection from './o2ocall/CallCreatorSection';
import VerifiedBadge from './VerifiedBadge';
import { langFlag, MODE_LABELS, fmtAvgDuration } from '../lib/creatorMeta';

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground ${className}`}>
    {children}
  </span>
);

const CardHeader = ({ creator, subtitle, navigate }) => (
  <header className="flex items-center gap-3 px-3 py-2.5">
    <button onClick={() => navigate(`/u/${creator.username}`)} className="flex items-center gap-3">
      <div className="rounded-full bg-gradient-to-tr from-amber-300 via-yellow-400 to-amber-600 p-[2px]">
        <div className="rounded-full bg-card p-[1.5px]">
          {creator.avatar_url ? (
            <img src={creator.avatar_url} alt={creator.username} className="h-8 w-8 shrink-0 rounded-full object-cover object-center" loading="lazy" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
              {(creator.username || '?').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </div>
      <div className="text-left">
        <p className="flex items-center gap-1 text-sm font-semibold">
          {creator.username}
          {creator.is_verified_creator && <VerifiedBadge size={14} />}
        </p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </button>
  </header>
);

/**
 * Phase 55 · Auto-published feed cards:
 *   o2o_available — creator is available NOW for pay-per-minute calls
 *   o2o_ended     — creator went offline (visible ~1h)
 *   live_ended    — a live just finished (1h / 15min)
 */
const SystemFeedCard = ({ item }) => {
  const navigate = useNavigate();
  const { system_type: type, payload = {}, creator = {} } = item;
  const cover = payload.cover_url ? mediaUrl(payload.cover_url) : '';

  if (type === 'o2o_available') {
    const topics = payload.topics || creator.topics || [];
    const languages = payload.languages || creator.languages || [];
    const avg = fmtAvgDuration(payload.avg_call_seconds ?? creator.avg_call_seconds);
    return (
      <article className="mb-6 overflow-hidden border-b border-border bg-card md:rounded-lg md:border md:border-border" data-testid="system-card-o2o-available">
        <CardHeader creator={creator} subtitle="è disponibile ora per una chiamata" navigate={navigate} />
        <div className="relative">
          {cover ? (
            <img src={cover} alt="" className="aspect-video w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center bg-gradient-to-br from-emerald-800 via-neutral-900 to-black">
              <Phone className="h-16 w-16 text-emerald-400/40" strokeWidth={1.2} />
            </div>
          )}
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Disponibile ora
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5 px-3 pt-3">
          {payload.rate_credits_per_min > 0 && (
            <Chip className="bg-amber-500/15 gold-text"><Coins className="h-3 w-3" /> {payload.rate_credits_per_min} crediti/min</Chip>
          )}
          {payload.o2o_mode && (
            <Chip>{payload.o2o_mode === 'audio' ? <Mic className="h-3 w-3" /> : <Video className="h-3 w-3" />} {MODE_LABELS[payload.o2o_mode]?.replace(/^..\s/, '') || payload.o2o_mode}</Chip>
          )}
          {avg && <Chip><Clock className="h-3 w-3" /> Durata media {avg}</Chip>}
          {languages.length > 0 && <Chip>{languages.map(langFlag).join(' ')}</Chip>}
        </div>
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pt-2" data-testid="system-card-topics">
            {topics.map((t) => (
              <span key={t} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-[10px] font-semibold gold-text">{t}</span>
            ))}
          </div>
        )}
        <div className="px-3 py-3">
          {/* Full call CTA (state dot + Chiama il creator + waitlist) */}
          <CallCreatorSection creatorId={creator.id} username={creator.username} />
        </div>
      </article>
    );
  }

  if (type === 'o2o_ended') {
    return (
      <article className="mb-6 overflow-hidden border-b border-border bg-card md:rounded-lg md:border md:border-border" data-testid="system-card-o2o-ended">
        <CardHeader creator={creator} subtitle="chiamate terminate" navigate={navigate} />
        <div className="relative">
          {cover ? (
            <img src={cover} alt="" className="aspect-video w-full object-cover opacity-60 grayscale" loading="lazy" />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center bg-neutral-900">
              <PhoneOff className="h-14 w-14 text-neutral-600" strokeWidth={1.2} />
            </div>
          )}
        </div>
        <p className="px-3 py-3 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">@{creator.username}</span> non è più disponibile. Le chiamate sono terminate per oggi.
        </p>
      </article>
    );
  }

  if (type === 'live_ended') {
    return (
      <article className="mb-6 overflow-hidden border-b border-border bg-card md:rounded-lg md:border md:border-border" data-testid="system-card-live-ended">
        <CardHeader creator={creator} subtitle="la live è terminata" navigate={navigate} />
        <div className="relative">
          {cover ? (
            <img src={cover} alt={payload.title || ''} className="aspect-video w-full object-cover opacity-75" loading="lazy" />
          ) : (
            <div className="flex aspect-video w-full items-center justify-center bg-gradient-to-br from-red-900/40 via-neutral-900 to-black">
              <Radio className="h-14 w-14 text-red-400/40" strokeWidth={1.2} />
            </div>
          )}
          <span className="absolute left-3 top-3 rounded-full bg-neutral-800/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-neutral-300">
            Live terminata
          </span>
        </div>
        <p className="px-3 py-3 text-sm text-muted-foreground">
          La Live di <span className="font-semibold text-foreground">@{creator.username}</span>
          {payload.title ? ` "${payload.title}"` : ''} è terminata. Grazie a tutti per aver partecipato. 🙏
        </p>
      </article>
    );
  }

  return null;
};

export default React.memo(SystemFeedCard);
